/**
 * CSV Export Utility Service
 * Handles conversion of table data to CSV format and triggers downloads
 */

/**
 * Escapes and formats a cell value for CSV output
 * @param {any} value - The value to format
 * @returns {string} - Formatted CSV cell value
 */
export const formatCsvCell = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  // Convert to string and handle special cases
  let cellValue = String(value);
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Handle numbers - format to reasonable precision
  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return cellValue;
    } else {
      return value.toFixed(2);
    }
  }
  
  // Escape quotes and wrap in quotes if necessary
  if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
    cellValue = cellValue.replace(/"/g, '""');
    return `"${cellValue}"`;
  }
  
  return cellValue;
};

/**
 * Converts array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} headers - Array of header objects with key and label properties
 * @returns {string} - CSV formatted string
 */
export const arrayToCsv = (data, headers) => {
  if (!data || data.length === 0) {
    return '';
  }
  
  // Create header row
  const headerRow = headers.map(header => formatCsvCell(header.label || header.key));
  const csvRows = [headerRow.join(',')];
  
  // Process data rows
  data.forEach(row => {
    const csvRow = headers.map(header => {
      const value = row[header.key];
      return formatCsvCell(value);
    });
    csvRows.push(csvRow.join(','));
  });
  
  return csvRows.join('\n');
};

/**
 * Converts pivot table data (with dates as columns) to CSV
 * @param {Array} data - Array of devices with date columns
 * @param {Array} dates - Array of date strings
 * @param {Array} fixedHeaders - Array of fixed column headers
 * @param {Function} valueFormatter - Optional function to format cell values
 * @returns {string} - CSV formatted string
 */
export const pivotTableToCsv = (data, dates, fixedHeaders, valueFormatter = null) => {
  if (!data || data.length === 0) {
    return '';
  }
  
  // Create header row with fixed columns + dates
  const headerRow = [...fixedHeaders, ...dates.map(date => new Date(date).toLocaleDateString())];
  const csvRows = [headerRow.map(h => formatCsvCell(h)).join(',')];
  
  // Process data rows
  data.forEach(device => {
    const row = [];
    
    // Add fixed column values
    fixedHeaders.forEach(header => {
      row.push(formatCsvCell(device[header]));
    });
    
    // Add date column values
    dates.forEach(date => {
      const cellData = device[date];
      let value = '';
      
      if (cellData !== null && cellData !== undefined) {
        if (valueFormatter && typeof valueFormatter === 'function') {
          value = valueFormatter(cellData);
        } else if (typeof cellData === 'object') {
          // Handle complex objects (like success rate data)
          if (cellData.successPercentage !== undefined) {
            value = `${cellData.successPercentage}%`;
          } else if (cellData.partialSuccessPercentage !== undefined && cellData.binarySuccessPercentage !== undefined) {
            value = `${cellData.partialSuccessPercentage}% / ${cellData.binarySuccessPercentage}%`;
          } else if (cellData.connected !== undefined) {
            value = cellData.connected ? 'Connected' : 'Not Connected';
          } else if (cellData.medianVoltage !== undefined) {
            value = `${cellData.medianVoltage}V`;
          } else if (cellData.minTemperature !== undefined && cellData.maxTemperature !== undefined) {
            value = `${cellData.minTemperature}°C to ${cellData.maxTemperature}°C`;
          } else {
            value = JSON.stringify(cellData);
          }
        } else {
          value = cellData;
        }
      }
      
      row.push(formatCsvCell(value));
    });
    
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
};

/**
 * Converts hourly data to CSV format
 * @param {Array} hourlyData - Array with 24 hour objects
 * @returns {string} - CSV formatted string
 */
export const hourlyDataToCsv = (hourlyData, timezone = null) => {
  console.log('🔧 hourlyDataToCsv called with data:', hourlyData, 'timezone:', timezone);
  
  if (!hourlyData || hourlyData.length === 0) {
    console.error('❌ No hourly data provided to hourlyDataToCsv');
    return '';
  }
  
  const headers = ['Hour', 'Median Usage', 'Total Usage', 'Data Points', 'Devices'];
  const csvRows = [];
  
  // Add timezone information header if provided
  if (timezone) {
    csvRows.push(`# Hourly Usage Data - Timezone: ${timezone}`);
    csvRows.push(''); // Empty row for separation
  }
  
  csvRows.push(headers.map(h => formatCsvCell(h)).join(','));
  
  hourlyData.forEach((hourDataEntry, index) => {
    console.log(`🔧 Processing hourly entry ${index}:`, hourDataEntry);
    const row = [
      formatCsvCell(hourDataEntry.hour || `${index.toString().padStart(2, '0')}:00`),
      formatCsvCell(hourDataEntry.median || 0),
      formatCsvCell(hourDataEntry.total || 0),
      formatCsvCell(hourDataEntry.count || 0),
      formatCsvCell(hourDataEntry.deviceCount || 0)
    ];
    csvRows.push(row.join(','));
  });
  
  const result = csvRows.join('\n');
  console.log('🔧 Generated CSV:', result);
  return result;
};

/**
 * Converts daily consumption data to CSV format
 * @param {Array} reportData - Array of device data with dailyUsage
 * @param {Array} sortedDates - Array of sorted date strings
 * @returns {string} - CSV formatted string
 */
export const dailyConsumptionToCsv = (reportData, sortedDates) => {
  const headers = ['Device', ...sortedDates.map(date => new Date(date).toLocaleDateString())];
  const csvRows = [headers.map(h => formatCsvCell(h)).join(',')];
  
  // Add device rows
  reportData.forEach(device => {
    const row = [device.serialNumber];
    sortedDates.forEach(date => {
      const dayData = device.dailyUsage?.find(day => day.date === date);
      row.push(formatCsvCell(dayData ? dayData.consumption : 'N/A'));
    });
    csvRows.push(row.join(','));
  });
  
  // Add total row
  const totalRow = ['Total'];
  sortedDates.forEach(date => {
    const totalForDate = reportData.reduce((sum, device) => {
      const dayData = device.dailyUsage?.find(day => day.date === date);
      return sum + (dayData ? dayData.consumption : 0);
    }, 0);
    totalRow.push(formatCsvCell(totalForDate));
  });
  csvRows.push(totalRow.join(','));
  
  return csvRows.join('\n');
};

/**
 * Triggers download of CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name for the downloaded file (without .csv extension)
 */
export const downloadCsv = (csvContent, filename) => {
  // Add BOM for proper Excel UTF-8 handling
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Generates timestamp-based filename
 * @param {string} baseName - Base name for the file
 * @returns {string} - Filename with timestamp
 */
export const generateTimestampedFilename = (baseName) => {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `${baseName}_${timestamp}`;
};

/**
 * Main export function for different table types
 * @param {string} tableType - Type of table to export
 * @param {Object} data - Data to export
 * @param {Object} options - Export options
 */
export const exportTableData = (tableType, data, options = {}) => {
  console.log('🔧 exportTableData called with:', tableType, data);
  let csvContent = '';
  let filename = '';
  
  // Check if data is undefined or empty
  if (!data) {
    console.error('❌ Export failed - no data provided');
    return false;
  }
  
  // Check if pivotData is empty for pivot table exports
  if (data.pivotData !== undefined && (!data.pivotData || data.pivotData.length === 0)) {
    console.error('❌ Export failed - pivotData is empty');
    return false;
  }
  
  switch (tableType) {
    case 'hourly-usage':
      console.log('🔧 Processing hourly-usage export, hourlyData:', data.hourlyData, 'timezone:', data.timezone);
      csvContent = hourlyDataToCsv(data.hourlyData, data.timezone);
      filename = generateTimestampedFilename('hourly_usage_data');
      console.log('🔧 Generated CSV content length:', csvContent.length);
      break;
      
    case 'daily-consumption':
      csvContent = dailyConsumptionToCsv(data.reportData, data.sortedDates);
      filename = generateTimestampedFilename('daily_consumption');
      break;
      
    case 'sensor-summary':
      const sensorHeaders = [
        { key: 'serialNumber', label: 'Serial Number' },
        { key: 'meterName', label: 'Meter Name' },
        { key: 'lastConnected', label: 'Last Connected' },
        { key: 'lastMeterValue', label: 'Last Meter Value' },
        { key: 'lastBatteryVoltage', label: 'Battery Voltage (V)' },
        { key: 'lastRSSI', label: 'Last RSSI' },
        { key: 'lastTemperature', label: 'Temperature (°C)' }
      ];
      csvContent = arrayToCsv(data.sensorData, sensorHeaders);
      filename = generateTimestampedFilename('sensor_summary');
      break;
      
    case 'success-rate-pivot':
      console.log('🔧 Processing success-rate-pivot export, pivotData:', data.pivotData, 'dates:', data.dates);
      if (!data.pivotData || data.pivotData.length === 0) {
        console.error('❌ No pivot data available for success rate export');
        return false;
      }
      if (!data.dates || data.dates.length === 0) {
        console.error('❌ No dates available for success rate export');
        return false;
      }
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData ? `${cellData.partialSuccessPercentage}% / ${cellData.binarySuccessPercentage}%` : 'N/A'
      );
      filename = generateTimestampedFilename('success_rate_daily');
      break;
      
    case 'connectivity-pivot':
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData ? (cellData.connected ? 'Connected' : 'Not Connected') : 'N/A'
      );
      filename = generateTimestampedFilename('connectivity_daily');
      break;
      
    case 'rssi-pivot':
      console.log('🔧 Processing rssi-pivot export, pivotData:', data.pivotData, 'dates:', data.dates);
      if (!data.pivotData || data.pivotData.length === 0) {
        console.error('❌ No pivot data available for RSSI export');
        return false;
      }
      if (!data.dates || data.dates.length === 0) {
        console.error('❌ No dates available for RSSI export');
        return false;
      }
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData ? `${cellData}` : 'N/A'
      );
      filename = generateTimestampedFilename('rssi_daily');
      break;
      
    case 'battery-pivot':
      console.log('🔧 Processing battery-pivot export, pivotData:', data.pivotData, 'dates:', data.dates);
      if (!data.pivotData || data.pivotData.length === 0) {
        console.error('❌ No pivot data available for battery export');
        return false;
      }
      if (!data.dates || data.dates.length === 0) {
        console.error('❌ No dates available for battery export');
        return false;
      }
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData && cellData.medianVoltage !== null ? `${cellData.medianVoltage}V` : 'N/A'
      );
      filename = generateTimestampedFilename('battery_voltage_daily');
      break;
      
    case 'temperature-pivot':
      console.log('🔧 Processing temperature-pivot export, pivotData:', data.pivotData, 'dates:', data.dates);
      if (!data.pivotData || data.pivotData.length === 0) {
        console.error('❌ No pivot data available for temperature export');
        return false;
      }
      if (!data.dates || data.dates.length === 0) {
        console.error('❌ No dates available for temperature export');
        return false;
      }
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => {
          if (cellData && cellData.minTemperature !== null && cellData.maxTemperature !== null) {
            return `${cellData.minTemperature}°C to ${cellData.maxTemperature}°C`;
          }
          return 'N/A';
        }
      );
      filename = generateTimestampedFilename('temperature_daily');
      break;
      
    case 'boot-reason-pivot':
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData || 'N/A'
      );
      filename = generateTimestampedFilename('boot_reason_daily');
      break;
      
    case 'shadow-updates-pivot':
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData || 'N/A'
      );
      filename = generateTimestampedFilename('shadow_updates_daily');
      break;
      
    case 'image-uploads-pivot':
      csvContent = pivotTableToCsv(
        data.pivotData, 
        data.dates, 
        ['serialNumber', 'meterName'],
        (cellData) => cellData || 'N/A'
      );
      filename = generateTimestampedFilename('image_uploads_daily');
      break;
      
    case 'daily-data-payloads-heatmap':
    case 'daily-transmission-events-heatmap':
      // Handle heatmap data - assuming it has devices and dates structure
      if (data.devices && data.dates) {
        // Create header row with fixed columns + dates
        const headerRow = ['Device Serial', 'Device Name', ...data.dates.map(date => new Date(date).toLocaleDateString())];
        const csvRows = [headerRow.map(h => formatCsvCell(h)).join(',')];
        
        // Process data rows
        data.devices.forEach(device => {
          const row = [];
          row.push(formatCsvCell(device['Device Serial']));
          row.push(formatCsvCell(device['Device Name']));
          
          // Add date column values
          data.dates.forEach(date => {
            const value = device[date] || 0;
            row.push(formatCsvCell(value));
          });
          
          csvRows.push(row.join(','));
        });
        
        csvContent = csvRows.join('\n');
        filename = generateTimestampedFilename(tableType.replace('-heatmap', '').replace(/-/g, '_'));
      } else {
        console.error('Heatmap data missing devices or dates structure:', data);
        return false;
      }
      break;
      
    default:
      console.error('Unknown table type:', tableType);
      return;
  }
  
  console.log('🔧 Final csvContent length:', csvContent.length, 'filename:', filename);
  
  if (csvContent) {
    console.log('🔧 Calling downloadCsv...');
    downloadCsv(csvContent, filename);
    console.log('✅ Export successful');
    return true; // Indicate successful export
  }
  
  console.error('❌ Export failed - no CSV content generated');
  return false; // Indicate failed export
}; 